'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'

export interface HowStep {
  n: number
  mp4: string
  poster: string
  label: string
  title: string
  body: string
}

interface HowItWorksPlayerProps {
  steps: HowStep[]
}

/**
 * Sequential step player: the three clips play one at a time — step 1 finishes,
 * step 2 begins — cycling forever while in view. The active card lifts with a
 * primary ring and its number pill fills in. One centred column on mobile,
 * three-up on desktop. Reduced-motion users get still posters, no auto-advance.
 */
export function HowItWorksPlayer({ steps }: HowItWorksPlayerProps) {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [active, setActive] = useState<number>(0)
  const [playing, setPlaying] = useState<number | null>(null)

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const visible = new Set<number>()
    let loaded = document.readyState === 'complete'

    const sync = (): void => {
      videoRefs.current.forEach((video, index) => {
        if (!video) return
        if (
          visible.has(index) &&
          loaded &&
          !motion.matches &&
          !document.hidden &&
          index === active
        ) {
          // Withhold the source, not just playback: preload="metadata" still
          // transfers video bytes before a visitor reaches this section.
          if (!video.getAttribute('src')) video.src = steps[index].mp4
          video.play().catch(() => {})
        } else {
          video.pause()
        }
      })
    }
    const onLoad = (): void => {
      loaded = true
      sync()
    }
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = videoRefs.current.indexOf(
            entry.target as HTMLVideoElement
          )
          if (entry.isIntersecting) visible.add(index)
          else visible.delete(index)
        }
        // Mobile stacks the cards. Keep playback on a visible card instead
        // of advancing into a video further down the page.
        if (visible.size > 0 && !visible.has(active)) {
          setActive(Math.min(...visible))
        }
        sync()
      },
      { rootMargin: '80px 0px' }
    )
    for (const video of videoRefs.current) {
      if (video) observer.observe(video)
    }
    if (!loaded) window.addEventListener('load', onLoad, { once: true })
    motion.addEventListener('change', sync)
    document.addEventListener('visibilitychange', sync)
    const videos = videoRefs.current
    return () => {
      observer.disconnect()
      window.removeEventListener('load', onLoad)
      motion.removeEventListener('change', sync)
      document.removeEventListener('visibilitychange', sync)
      for (const video of videos) video?.pause()
    }
  }, [active, steps])

  const advance = (): void => {
    setActive((index) => (index + 1) % steps.length)
  }

  return (
    <ol className="grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8">
      {steps.map((step, index) => {
        const isActive = index === active
        return (
          <li
            key={step.n}
            className="mx-auto w-full max-w-[340px] text-center sm:max-w-none"
          >
            <div className="relative">
              <div
                className={cn(
                  'shadow-float relative overflow-hidden rounded-2xl border transition-[border-color,opacity] duration-500 motion-reduce:transition-none',
                  isActive ? 'border-primary/50' : 'border-border opacity-90'
                )}
              >
                <video
                  ref={(node) => {
                    videoRefs.current[index] = node
                  }}
                  muted
                  playsInline
                  preload="none"
                  aria-label={step.label}
                  onEnded={advance}
                  onPlaying={() => setPlaying(index)}
                  onPause={() =>
                    setPlaying((current) =>
                      current === index ? null : current
                    )
                  }
                  width={1108}
                  height={828}
                  className="bg-surface h-auto w-full"
                />
                <Image
                  src={step.poster}
                  alt=""
                  aria-hidden
                  width={1108}
                  height={828}
                  sizes="(min-width: 640px) 320px, 340px"
                  className={cn(
                    'pointer-events-none absolute inset-0 h-full w-full object-cover',
                    playing === index ? 'opacity-0' : 'opacity-100'
                  )}
                />
              </div>
              <span
                className={cn(
                  'absolute -top-2.5 -left-2.5 z-10 flex h-9 w-9 items-center justify-center rounded-full border font-mono text-sm font-bold shadow-sm transition-colors duration-500',
                  isActive
                    ? 'border-primary bg-primary text-white'
                    : 'border-primary-border/60 bg-bg text-primary'
                )}
              >
                {step.n}
              </span>
            </div>
            <h3 className="font-heading text-ink mt-5 text-xl font-semibold">
              {step.title}
            </h3>
            <p className="text-body mx-auto mt-2 max-w-xs text-[15px] leading-relaxed">
              {step.body}
            </p>
          </li>
        )
      })}
    </ol>
  )
}
